import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState("");
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setDemo(!!data.demo_data)).catch(() => {});
  }, []);

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: unverified });
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doLogin = async (e, creds) => {
    if (e) e.preventDefault();
    setError("");
    setUnverified("");
    setLoading(true);
    try {
      const c = creds || { email, password };
      await login(c.email, c.password);
      toast.success("Welcome to Skanovix HRMS");
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && d.code === "EMAIL_NOT_VERIFIED") {
        setUnverified((creds || { email }).email);
        setError(d.message);
      } else {
        setError(apiError(d) || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-body bg-background">
      {/* Left hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?auto=format&fit=crop&w=1200&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-emerald-950/70" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center overflow-hidden border border-white/20">
            <img src="/logo.jpg" alt="Skanovix logo" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg tracking-tight">Skanovix Info Tech Solutions</p>
            <p className="text-xs text-white/60 font-mono uppercase tracking-widest">HR Enterprise Suite</p>
          </div>
        </div>
        <div className="relative space-y-4">
          <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
            From Recruitment<br />to Relieving.
          </h2>
          <p className="text-white/70 max-w-md">
            Auto-generated offer letters with India statutory CTC breakdown, attendance timesheets,
            payroll payslips and complete exit management — one intelligent platform.
          </p>
          <div className="flex gap-6 pt-4 text-sm">
            {["EPF 12%", "ESI 0.75%"].map((t) => (
              <div key={t} className="font-mono text-emerald-300">{t}</div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-white/40 font-mono">© 2026 Skanovix Info Tech Solutions</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-in-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center">
              <img src="/logo.jpg" alt="Skanovix logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-heading font-extrabold text-lg">Skanovix HRMS</span>
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-1 mb-8">Sign in to your HR portal account.</p>

          <form onSubmit={(e) => doLogin(e)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work Email</Label>
              <Input
                id="email" type="email" value={email} data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)} placeholder="you@skanovix.com" required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input
                id="password" type="password" value={password} data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                className="h-11"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            {unverified && (
              <button type="button" onClick={resend} className="text-sm text-primary underline" data-testid="resend-verification-button">
                Resend verification email
              </button>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 rounded-lg font-semibold active:scale-[0.98] transition-transform">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          {demo && (
          <div className="mt-8" data-testid="quick-demo-login">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 text-center">Quick demo login</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button" data-testid="quick-login-hr-button"
                onClick={() => doLogin(null, { email: "admin@skanovix.com", password: "Skanovix@2026" })}
                className="flex flex-col items-start gap-1 p-3 rounded-xl border border-border hover:border-primary hover:bg-secondary transition-all text-left"
              >
                <ShieldCheck className="w-5 h-5 text-accent" />
                <span className="text-sm font-semibold">HR Administrator</span>
                <span className="text-[11px] text-muted-foreground">Full access</span>
              </button>
              <button
                type="button" data-testid="quick-login-employee-button"
                onClick={() => doLogin(null, { email: "rahul.verma@skanovix.com", password: "Employee@123" })}
                className="flex flex-col items-start gap-1 p-3 rounded-xl border border-border hover:border-primary hover:bg-secondary transition-all text-left"
              >
                <UserRound className="w-5 h-5 text-chart-2" />
                <span className="text-sm font-semibold">Employee</span>
                <span className="text-[11px] text-muted-foreground">Rahul Verma</span>
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
